import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import SkeletonLoader from '../components/SkeletonLoader'
import { decodeEntities } from '../lib/decodeEntities'

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return session ? { Authorization: `Bearer ${session.access_token}` } : {}
}

export default function RequestsPage() {
  const { user } = useAuth()
  const [requests, setRequests] = useState([])
  const [loading, setLoading]   = useState(true)
  const [busy, setBusy]         = useState(new Set())
  const [error, setError]       = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const headers = await getHeaders()
      const { data } = await axios.get('/api/follow-requests/inbox', { headers })
      setRequests(data ?? [])
    } catch (err) {
      console.error('[RequestsPage] inbox fetch failed:', err)
      setError('Failed to load requests. Please refresh and try again.')
    }
    setLoading(false)
  }, [])

  useEffect(() => { if (user) load() }, [user, load])

  async function handleAccept(reqId) {
    if (busy.has(reqId)) return
    setBusy(prev => new Set(prev).add(reqId))
    setError(null)
    // Optimistic: remove from list immediately
    const snapshot = requests
    setRequests(prev => prev.filter(r => r.id !== reqId))
    try {
      const headers = await getHeaders()
      await axios.post(`/api/follow-requests/${reqId}/accept`, {}, { headers })
    } catch (err) {
      console.error('[RequestsPage] accept failed:', err)
      setError('Action failed. Please try again.')
      setRequests(snapshot)
    } finally {
      setBusy(prev => { const s = new Set(prev); s.delete(reqId); return s })
    }
  }

  async function handleReject(reqId) {
    if (busy.has(reqId)) return
    setBusy(prev => new Set(prev).add(reqId))
    setError(null)
    const snapshot = requests
    setRequests(prev => prev.filter(r => r.id !== reqId))
    try {
      const headers = await getHeaders()
      await axios.post(`/api/follow-requests/${reqId}/reject`, {}, { headers })
    } catch (err) {
      console.error('[RequestsPage] reject failed:', err)
      setError('Action failed. Please try again.')
      setRequests(snapshot)
    } finally {
      setBusy(prev => { const s = new Set(prev); s.delete(reqId); return s })
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 1rem' }}>
        <h1 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Follow requests</h1>

        {error && <div className="auth-error" style={{ marginBottom: '1rem' }}>{error}</div>}

        {loading && <SkeletonLoader count={3} />}

        {!loading && requests.length === 0 && (
          <p style={{ color: 'var(--text-faint)', textAlign: 'center', padding: '3rem 1rem' }}>
            No pending follow requests.
          </p>
        )}

        {!loading && requests.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {requests.map(r => {
              const isBusy = busy.has(r.id)
              return (
                <li
                  key={r.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.75rem 1rem',
                    borderBottom: '1px solid var(--border-faint)',
                  }}
                >
                  <Link
                    to={`/user/${r.requester.username}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      flex: 1,
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: r.requester.avatarUrl ? undefined : (r.requester.avatarGrad ?? 'linear-gradient(135deg, #7C3AED, #EC4899)'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      {r.requester.avatarUrl
                        ? <img src={r.requester.avatarUrl} alt={r.requester.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                        : (r.requester.displayName?.[0] ?? '?').toUpperCase()}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{decodeEntities(r.requester.displayName)}</span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-faint)' }}>@{r.requester.username}</span>
                    </div>
                  </Link>

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleAccept(r.id)}
                      disabled={isBusy}
                    >
                      {isBusy ? '…' : 'Accept'}
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => handleReject(r.id)}
                      disabled={isBusy}
                    >
                      {isBusy ? '…' : 'Decline'}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
